import { FC, Fragment, useMemo } from "react"
import ChangesTable from "./ChangesTable"
import PrioritisedChangeItem from "./PrioritisedChangeItem"
import { LogChangeItem } from "@/api/LogsService"
import { IconProps } from "@/components/shared/Icon"
import { AlertVariant } from "@/types/alert"
import { capitalizeFirstLetter } from "@/utils/index"
import { isBoolean } from "@/utils/lodash"

type ChangeProps = {
   changes: LogChangeItem[]
   type?: "respiratoryRate" | "heartRate"
   getAlertVariantClassName: (alertVariant: keyof typeof AlertVariant) => string
}

const Changes: FC<ChangeProps> = ({
   changes,
   type,
   getAlertVariantClassName,
}) => {
   const thresholdIconName: IconProps["name"] = useMemo(() => {
      switch (type) {
         case "heartRate":
            return "heartRate"
         case "respiratoryRate":
            return "respiratoryRate"
         default:
            return "threshold"
      }
   }, [type])

   const priorities = useMemo(() => ["priority", "name"], [])

   const prioritisedChanges = useMemo(
      () => changes.filter((c) => priorities.includes(c.field)),
      [changes, priorities]
   )

   const otherChanges = useMemo(
      () =>
         changes
            .filter((c) => !prioritisedChanges.includes(c))
            .filter(
               (c) =>
                  c.field !== "maintenance" &&
                  c.field !== "vacant" &&
                  c.field !== "enabled"
            ),
      [changes, prioritisedChanges]
   )

   if (prioritisedChanges.length === 0 && otherChanges.length === 0) return null

   return (
      <>
         {prioritisedChanges.map((change, i) => {
            const { oldValue, field, newValue } = change
            return (
               <Fragment key={i}>
                  {oldValue && !isBoolean(oldValue) && (
                     <PrioritisedChangeItem
                        title={`Previous ${capitalizeFirstLetter(field)}`}
                        value={oldValue as string | number}
                     />
                  )}
                  {!isBoolean(newValue) && (
                     <PrioritisedChangeItem
                        title={`${oldValue ? "New" : ""} ${capitalizeFirstLetter(field)}`}
                        value={newValue as string | number}
                     />
                  )}
               </Fragment>
            )
         })}
         <ChangesTable
            changes={otherChanges}
            type={type}
            thresholdIconName={thresholdIconName}
            getAlertVariantClassName={getAlertVariantClassName}
         />
      </>
   )
}

export default Changes
